/**
 * LOXORD mesh atmosphere — the shader world.
 *
 * The exact engine skylrk.com runs: the "Lava Lamp" fragment shader by
 * welches (https://www.shadertoy.com/view/DdcfzH — credited here as skylrk
 * credits it), fed with this theme's four cloud colours per world instead of
 * a palette derived from one hex. Same math, same tempo, same grain.
 *
 * This replaces the CSS blurred-composite field wherever WebGL is available:
 * the shader COMPUTES the flowing gradient per pixel in one draw call, where
 * the CSS field simulates it with a large blur re-evaluated every frame. The
 * CSS field stays in the markup as the fallback — if this file fails to run,
 * a context is refused, or the context is lost, the world still renders.
 *
 * Uniform mapping (per world):
 *   uTopColor    ← cloud 1      uBottomColor ← cloud 2
 *   uAccentColor ← cloud 3      uDarkColor   ← cloud 4
 *
 * Merchant settings arrive as the custom properties the atmosphere already
 * carries: --cloud-speed multiplies time, --cloud-travel scales the warp
 * (100% = the reference exactly), --field-grain drives the animated grain,
 * --field-opacity dims the canvas.
 */
(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const VERTEX = `
attribute vec2 position;
varying vec2 vTextureCoord;
void main() {
  vTextureCoord = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

  /* The lava-lamp shader, unchanged except for two multipliers:
     uSwell scales the sine-warp amplitude (1.0 = verbatim) and uGrain carries
     the animated grain pass skylrk applies as a second render target — folded
     into this single pass with the same gaussian formula. */
  const FRAGMENT = `
precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform vec3 uTopColor;
uniform vec3 uBottomColor;
uniform vec3 uAccentColor;
uniform vec3 uDarkColor;
uniform vec2 uFocusPoint;
uniform float uFocusStrength;
uniform float uSwell;
uniform float uGrain;

varying vec2 vTextureCoord;

#define filmGrainIntensity 0.1

mat2 Rot(float a) {
  float s = sin(a);
  float c = cos(a);
  return mat2(c, -s, s, c);
}

vec2 hash(vec2 p) {
  p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
  return fract(sin(p) * 43758.5453);
}

float noise(in vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
                mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
  return 0.5 + 0.5 * n;
}

float filmGrainNoise(in vec2 uv) {
  return length(hash(vec2(uv.x, uv.y)));
}

float gaussian(float z, float u, float o) {
  return (1.0 / (o * sqrt(2.0 * 3.1415))) * exp(-(((z - u) * (z - u)) / (2.0 * (o * o))));
}

void main() {
  vec2 uv = vTextureCoord;
  float aspectRatio = uResolution.x / uResolution.y;

  vec2 tuv = uv - 0.5;

  float t = uTime * 0.5;

  // Rotate with noise
  float degree = noise(vec2(t * 0.05, tuv.x * tuv.y));
  tuv.y *= 1.0 / aspectRatio;
  tuv *= Rot(radians((degree - 0.5) * 720.0 + 180.0));
  tuv.y *= aspectRatio;

  // Wave warp with sine
  float frequency = 5.0;
  float amplitude = 30.0 / max(uSwell, 0.0001);
  float speed = t * 2.0;
  tuv.x += sin(tuv.y * frequency + speed) / amplitude;
  tuv.y += sin(tuv.x * frequency * 1.5 + speed) / (amplitude * 0.5);

  vec3 color1 = uTopColor;
  vec3 color2 = uDarkColor;
  vec3 color3 = uAccentColor;
  vec3 color4 = uBottomColor;

  // Flat gradient (original 4-color lava lamp blend)
  vec3 layer1 = mix(color3, color2, smoothstep(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
  vec3 layer2 = mix(color4, color1, smoothstep(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
  vec3 flatColor = mix(layer1, layer2, smoothstep(0.5, -0.3, tuv.y));

  // Radial brightness boost at focus point
  vec2 warp = tuv - (uv - 0.5);
  vec2 focusPt = uFocusPoint - 0.5;
  vec2 delta = (uv - 0.5) - focusPt + warp * 0.5;
  delta.x *= aspectRatio;
  float d = length(delta);
  float radialMask = smoothstep(0.6, 0.0, d) * uFocusStrength;
  vec3 col = mix(flatColor, mix(flatColor, color1, 0.45), radialMask);

  // Film grain (the shader's own, subtractive)
  col = col - filmGrainNoise(uv) * filmGrainIntensity;

  // Animated grain — skylrk's second pass, same formula, folded in
  float gseed = dot(uv, vec2(12.9898, 78.233));
  float gnoise = fract(sin(gseed) * 43758.5453 + uTime * 2.0);
  gnoise = gaussian(gnoise, 0.0, 0.25);
  col += vec3(gnoise) * (1.0 - col) * uGrain;

  gl_FragColor = vec4(col, 1.0);
}
`;

  const hexToRgb = (hex) => {
    let h = (hex || '').trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
  };

  // the recolour ease — the JS twin of --ease-scene
  const easeScene = (t) => 1 - Math.pow(1 - t, 4);

  const worlds = new Map(); // atmosphere el -> world

  function initWorld(atmosphere) {
    if (!atmosphere || worlds.has(atmosphere)) return worlds.get(atmosphere) || null;

    const canvas = atmosphere.querySelector('.atmosphere__canvas');
    if (!canvas) return null;

    const gl = canvas.getContext('webgl', { antialias: false, depth: false, stencil: false, alpha: false })
      || canvas.getContext('experimental-webgl');
    if (!gl) return null;

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || 'shader');
      return sh;
    };

    let program;
    try {
      program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link');
    } catch (e) {
      return null; // fallback field stays visible
    }
    gl.useProgram(program);

    // one fullscreen triangle
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const U = {};
    for (const name of ['uTime', 'uResolution', 'uTopColor', 'uBottomColor', 'uAccentColor', 'uDarkColor',
      'uFocusPoint', 'uFocusStrength', 'uSwell', 'uGrain']) {
      U[name] = gl.getUniformLocation(program, name);
    }

    const styles = getComputedStyle(atmosphere);
    const readVar = (name, fallback) => {
      const v = parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(v) ? v : fallback;
    };

    const restColors = (atmosphere.dataset.restClouds || '').split('|').map(hexToRgb);
    const world = {
      atmosphere, canvas, gl, U,
      speed: readVar('--cloud-speed', 1),
      swell: readVar('--cloud-travel', 1),
      grain: readVar('--field-grain', 0.02),
      current: restColors.map((c) => (c ? c.slice() : [0, 0, 0])),
      from: null, target: null, tweenStart: 0, tweenMs: 0,
      time: 0, last: 0, raf: 0, running: false, lost: false,
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(atmosphere.clientWidth * dpr));
      const h = Math.max(1, Math.round(atmosphere.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const draw = (now) => {
      if (world.lost) return;
      const dt = world.last ? Math.min((now - world.last) / 1000, 0.1) : 0;
      world.last = now;
      world.time += dt * world.speed;

      // eased recolour toward the target world
      if (world.target) {
        const p = Math.min(1, (now - world.tweenStart) / world.tweenMs);
        const e = easeScene(p);
        for (let i = 0; i < 4; i += 1) {
          for (let c = 0; c < 3; c += 1) {
            world.current[i][c] = world.from[i][c] + (world.target[i][c] - world.from[i][c]) * e;
          }
        }
        if (p >= 1) world.target = null;
      }

      resize();
      gl.uniform1f(U.uTime, world.time);
      gl.uniform2f(U.uResolution, canvas.width, canvas.height);
      gl.uniform3fv(U.uTopColor, world.current[0]);
      gl.uniform3fv(U.uBottomColor, world.current[1]);
      gl.uniform3fv(U.uAccentColor, world.current[2]);
      gl.uniform3fv(U.uDarkColor, world.current[3]);
      gl.uniform2f(U.uFocusPoint, 0.5, 0.5);
      gl.uniform1f(U.uFocusStrength, 0);
      gl.uniform1f(U.uSwell, world.swell);
      gl.uniform1f(U.uGrain, world.grain);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      const still = reducedMotion.matches || world.swell === 0;
      if (world.running && !still && !document.hidden) {
        world.raf = requestAnimationFrame(draw);
      } else {
        world.running = false;
      }
    };

    world.start = () => {
      if (world.running || world.lost) return;
      world.running = true;
      world.last = 0;
      world.raf = requestAnimationFrame(draw);
    };
    world.drawOnce = () => { world.last = 0; requestAnimationFrame(draw); };

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      world.lost = true;
      cancelAnimationFrame(world.raf);
      atmosphere.classList.remove('atmosphere--gl'); // the CSS field takes over
    });

    // the shader owns the world now; the CSS field becomes the understudy
    atmosphere.classList.add('atmosphere--gl');
    worlds.set(atmosphere, world);
    if (reducedMotion.matches) world.drawOnce();
    else world.start();
    return world;
  }

  function setColors(atmosphere, hexes, ms) {
    const world = worlds.get(atmosphere);
    if (!world || world.lost) return;
    const target = world.current.map((cur, i) => hexToRgb(hexes[i]) || cur.slice());
    world.from = world.current.map((c) => c.slice());
    world.target = target;
    world.tweenStart = performance.now();
    world.tweenMs = Math.max(1, ms || 700);
    if (!world.running) {
      if (reducedMotion.matches) {
        world.current = target;
        world.target = null;
        world.drawOnce();
      } else {
        world.start();
      }
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    worlds.forEach((w) => { if (!reducedMotion.matches) w.start(); });
  });

  reducedMotion.addEventListener('change', () => {
    worlds.forEach((w) => {
      if (reducedMotion.matches) w.drawOnce();
      else w.start();
    });
  });

  const boot = () => document.querySelectorAll('.atmosphere').forEach((a) => initWorld(a));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // the motion engine recolours through this
  window.LoxordMesh = {
    init: initWorld,
    set: setColors,
    rest: (atmosphere, ms) => setColors(atmosphere, (atmosphere.dataset.restClouds || '').split('|'), ms),
  };
})();
